import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Mic, Check, ChevronRight, Volume2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";
import { MainLayout } from "@/components/layout/MainLayout";
import { getUserSettings, saveUserSettings } from "@/lib/userSettings";
import { useTranslation } from "@/hooks/useTranslation";

type SetupStage = "explanation" | "testing" | "complete";

// Configuration for robust operation
const CONFIG = {
  RESTART_DELAY_MS: 200,
  MAX_RESTART_ATTEMPTS: 5,
  RESTART_BACKOFF_MS: 1500,
};

export default function VoiceControlSetup() {
  const navigate = useNavigate();
  const [stage, setStage] = useState<SetupStage>("explanation");
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [selectedButton, setSelectedButton] = useState<number | null>(null);
  const [testsPassed, setTestsPassed] = useState(0);
  
  const recognitionRef = useRef<any>(null);
  const isRunningRef = useRef(false);
  const isMountedRef = useRef(true);
  const isStoppingRef = useRef(false);
  const restartAttemptsRef = useRef(0);
  const restartTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const settings = getUserSettings();
  const isFemale = settings.gender === "female";
  const { t, dir } = useTranslation();

  // Track mounted state
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Clear all timeouts
  const clearTimers = useCallback(() => {
    if (restartTimeoutRef.current) {
      clearTimeout(restartTimeoutRef.current);
      restartTimeoutRef.current = null;
    }
  }, []);

  // Safe stop recognition
  const stopRecognitionSafe = useCallback(() => {
    console.log('[VoiceSetup] Stopping recognition...');
    isStoppingRef.current = true;
    clearTimers();
    
    if (recognitionRef.current) {
      try {
        recognitionRef.current.onresult = null;
        recognitionRef.current.onerror = null;
        recognitionRef.current.onend = null;
        recognitionRef.current.onstart = null;
        recognitionRef.current.abort();
      } catch (e) {
        console.log('[VoiceSetup] Stop error:', e);
      }
    }
    
    isRunningRef.current = false;
    restartAttemptsRef.current = 0;
    
    setTimeout(() => {
      isStoppingRef.current = false;
    }, 100);
  }, [clearTimers]);

  // Safe start recognition
  const startRecognitionSafe = useCallback(() => {
    if (!isMountedRef.current) return;
    if (isRunningRef.current || isStoppingRef.current) return;
    if (!recognitionRef.current) return;

    // Backoff if too many attempts
    if (restartAttemptsRef.current >= CONFIG.MAX_RESTART_ATTEMPTS) {
      console.log('[VoiceSetup] Max attempts reached, backing off...');
      restartTimeoutRef.current = setTimeout(() => {
        restartAttemptsRef.current = 0;
        if (isMountedRef.current) {
          startRecognitionSafe();
        }
      }, CONFIG.RESTART_BACKOFF_MS);
      return;
    }

    try {
      console.log('[VoiceSetup] Starting recognition, attempt:', restartAttemptsRef.current + 1);
      recognitionRef.current.start();
      isRunningRef.current = true;
      restartAttemptsRef.current++;
    } catch (e: any) {
      console.log('[VoiceSetup] Start error:', e?.message);
      isRunningRef.current = false;
      
      if (e?.message?.includes('already started')) {
        isRunningRef.current = true;
        return;
      }
      
      restartTimeoutRef.current = setTimeout(() => {
        if (isMountedRef.current) {
          startRecognitionSafe();
        }
      }, CONFIG.RESTART_DELAY_MS);
    }
  }, []);

  // Handle result
  const handleResult = useCallback((event: any) => {
    if (!isMountedRef.current) return;
    
    restartAttemptsRef.current = 0;
    
    const last = event.results.length - 1;
    const text = event.results[last][0].transcript.trim();
    setTranscript(text);

    const numberMatch = text.match(/[0-9]+|אחת|אחד|שתיים|שניים|שלוש|ארבע|חמש/);
    if (numberMatch) {
      const hebrewToNumber: Record<string, number> = {
        'אחת': 1, 'אחד': 1,
        'שתיים': 2, 'שניים': 2,
        'שלוש': 3,
        'ארבע': 4,
        'חמש': 5
      };
      const num = hebrewToNumber[numberMatch[0]] || parseInt(numberMatch[0]);
      if (num >= 1 && num <= 4) {
        setSelectedButton(num);
        if (stage === "testing") {
          setTimeout(() => {
            setTestsPassed(prev => prev + 1);
            setSelectedButton(null);
          }, 1000);
        }
      }
    }
  }, [stage]);

  // Handle error
  const handleError = useCallback((event: any) => {
    console.log('[VoiceSetup] Recognition error:', event.error);
    isRunningRef.current = false;
    
    if (event.error === 'aborted' || isStoppingRef.current) return;
    
    const delay = event.error === 'no-speech' ? 100 : CONFIG.RESTART_DELAY_MS;
    restartTimeoutRef.current = setTimeout(() => {
      if (isMountedRef.current && !isRunningRef.current && !isStoppingRef.current) {
        startRecognitionSafe();
      }
    }, delay);
  }, [startRecognitionSafe]);

  // Handle end
  const handleEnd = useCallback(() => {
    console.log('[VoiceSetup] Recognition ended');
    isRunningRef.current = false;
    
    if (!isMountedRef.current || isStoppingRef.current) return;
    
    clearTimers();
    restartTimeoutRef.current = setTimeout(() => {
      if (isMountedRef.current && !isRunningRef.current && !isStoppingRef.current) {
        startRecognitionSafe();
      }
    }, CONFIG.RESTART_DELAY_MS);
  }, [startRecognitionSafe, clearTimers]);

  // Handle start
  const handleStart = useCallback(() => {
    console.log('[VoiceSetup] Recognition started');
    isRunningRef.current = true;
  }, []);

  // Initialize speech recognition
  useEffect(() => {
    const SpeechRecognitionAPI = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition;
    
    if (!SpeechRecognitionAPI) {
      console.log('[VoiceSetup] Speech recognition not supported');
      return;
    }

    try {
      const recognition = new SpeechRecognitionAPI();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'he-IL';
      recognition.maxAlternatives = 1;

      recognition.onresult = handleResult;
      recognition.onerror = handleError;
      recognition.onend = handleEnd;
      recognition.onstart = handleStart;

      recognitionRef.current = recognition;
    } catch (e) {
      console.log('[VoiceSetup] Failed to create recognition:', e);
    }

    return () => {
      stopRecognitionSafe();
      recognitionRef.current = null;
    };
  }, [handleResult, handleError, handleEnd, handleStart, stopRecognitionSafe]);

  const startListening = useCallback(() => {
    if (recognitionRef.current && !isRunningRef.current) {
      try {
        recognitionRef.current.start();
        isRunningRef.current = true;
        setIsListening(true);
        console.log('[VoiceSetup] Started listening');
      } catch (e) {
        console.error('Error starting recognition:', e);
        isRunningRef.current = false;
      }
    }
  }, []);

  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {}
      isRunningRef.current = false;
      setIsListening(false);
    }
  }, []);

  const handleBack = () => {
    if (stage === "explanation") {
      navigate("/control");
    } else if (stage === "testing") {
      stopListening();
      setStage("explanation");
    }
  };

  const handleContinue = () => {
    if (stage === "explanation") {
      setStage("testing");
      setTestsPassed(0);
      // Start listening when entering testing stage
      setTimeout(() => startListening(), 100);
    } else if (stage === "testing") {
      stopListening();
      // Save settings and navigate
      const settings = getUserSettings();
      saveUserSettings({
        ...settings,
        controlMethod: "voice",
      });
      navigate("/");
    }
  };

  const renderExplanationStage = () => (
    <div className="flex flex-col items-center w-full max-w-2xl">
      {/* Microphone icon */}
      <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center mb-6">
        <Mic className="w-8 h-8 text-primary-foreground" />
      </div>

      {/* Explanation text */}
      <p className="text-center text-foreground mb-8 text-lg">
        {isFemale ? t("voiceControlExplanationFemale") : t("voiceControlExplanation")}
      </p>

      {/* Demo buttons */}
      <div className="bg-muted/30 rounded-2xl p-6 w-full mb-6">
        <div className="grid grid-cols-2 gap-4">
          {[1, 2, 3, 4].map((num) => (
            <div key={num} className="relative">
              <div className="bg-card border border-border rounded-xl p-4 text-center">
                <span className="text-foreground font-medium">{t("buttonLabel")} {num}</span>
              </div>
              {/* Number badge */}
              <div className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-accent text-accent-foreground flex items-center justify-center text-sm font-bold shadow-lg">
                {num}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Example text */}
      <p className="text-muted-foreground text-sm text-center mb-4">
        {isFemale ? t("voiceControlExampleFemale") : t("voiceControlExample")}
      </p>

      {/* Wake word explanation */}
      <div className="bg-primary/10 border border-primary/30 rounded-xl p-4 w-full">
        <div className="flex items-center gap-2 mb-2">
          <Volume2 className="w-5 h-5 text-primary" />
          <span className="text-primary font-medium">{t("voiceControlWakeWord")}</span>
        </div>
        <p className="text-sm text-muted-foreground">
          {isFemale ? t("voiceControlWakeWordDescFemale") : t("voiceControlWakeWordDesc")}
        </p>
      </div>
    </div>
  );

  const renderTestingStage = () => (
    <div className="flex flex-col items-center w-full max-w-2xl">
      {/* Microphone icon - animated when listening */}
      <motion.div 
        className={cn(
          "w-16 h-16 rounded-full flex items-center justify-center mb-4",
          isListening ? "bg-primary" : "bg-muted"
        )}
        animate={isListening ? { scale: [1, 1.1, 1] } : {}}
        transition={{ repeat: Infinity, duration: 1.5 }}
      >
        <Mic className={cn(
          "w-8 h-8",
          isListening ? "text-primary-foreground" : "text-muted-foreground"
        )} />
      </motion.div>

      {/* Instructions */}
      <h2 className="text-xl font-bold text-foreground mb-2">{t("nowYourTurn")}</h2>
      <p className="text-muted-foreground text-center mb-6">
        {isFemale ? t("sayTheNumberFemale") : t("sayTheNumber")}
      </p>

      {/* Test buttons */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        {[1, 2, 3, 4].map((num) => {
          const isTarget = (testsPassed % 4) + 1 === num;
          const isSelected = selectedButton === num;
          
          return (
            <motion.div 
              key={num} 
              className="relative"
              animate={isSelected ? { scale: [1, 1.1, 1] } : {}}
            >
              <div className={cn(
                "rounded-xl p-6 text-center transition-all",
                isTarget
                  ? "bg-primary text-primary-foreground"
                  : "bg-card border border-border"
              )}>
                <span className="text-2xl font-bold">#</span>
              </div>
              {/* Number badge */}
              <div className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-accent text-accent-foreground flex items-center justify-center text-sm font-bold shadow-lg">
                {num}
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Transcript display */}
      {transcript && (
        <div className="flex items-center gap-2 mb-4">
          <Volume2 className="w-4 h-4 text-muted-foreground" />
          <span className="text-muted-foreground">{t("youSaid")} {transcript}</span>
        </div>
      )}

      {/* Listening indicator */}
      <motion.div 
        className={cn(
          "flex items-center gap-3 px-6 py-3 rounded-full",
          isListening ? "bg-card border border-border" : "bg-muted"
        )}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className={cn(
          "w-3 h-3 rounded-full",
          isListening ? "bg-primary animate-pulse" : "bg-muted-foreground"
        )} />
        <Mic className="w-5 h-5 text-muted-foreground" />
        <span className="text-muted-foreground">
          {isListening ? t("listeningActive") : t("clickToStart")}
        </span>
      </motion.div>

      {/* Start listening button */}
      {!isListening && (
        <button
          onClick={startListening}
          className="mt-4 px-6 py-3 bg-primary text-primary-foreground rounded-xl font-medium hover:bg-primary/90 transition-all"
        >
          {t("startTest")}
        </button>
      )}

      {/* Progress indicator */}
      {testsPassed > 0 && (
        <div className="mt-4 flex items-center gap-2">
          <span className="text-sm text-muted-foreground">{t("successes")}</span>
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className={cn(
                "w-3 h-3 rounded-full",
                i < testsPassed ? "bg-primary" : "bg-muted"
              )}
            />
          ))}
        </div>
      )}
    </div>
  );

  const getStageTitle = () => {
    switch (stage) {
      case "explanation": return t("voiceControlSetupTitle");
      case "testing": return t("voiceControlTestTitle");
      default: return t("voiceControlSetupTitle");
    }
  };

  const canContinue = stage === "explanation" || (stage === "testing" && testsPassed >= 3);

  return (
    <MainLayout>
      <div 
        className="flex-1 flex flex-col items-center p-4 bg-background overflow-auto"
        dir={dir}
      >
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center shadow-lg">
            <Mic className="w-6 h-6 text-primary-foreground" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-foreground">
              {getStageTitle()}
            </h1>
            <p className="text-xs text-muted-foreground">
              {t("voiceControlSubtitle")}
            </p>
          </div>
        </div>

        {/* Stage Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={stage}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="w-full flex flex-col items-center"
          >
            {stage === "explanation" && renderExplanationStage()}
            {stage === "testing" && renderTestingStage()}
          </motion.div>
        </AnimatePresence>

        {/* Action buttons */}
        <div className="flex gap-3 mt-auto pb-4">
          <button
            onClick={handleBack}
            className={cn(
              "flex items-center gap-2 px-6 py-3 rounded-xl transition-all text-base font-medium",
              "bg-card hover:bg-muted border-2 border-border",
              "text-muted-foreground hover:text-foreground"
            )}
          >
            <ChevronRight className="w-5 h-5" />
            <span>{t("back")}</span>
          </button>

          <button
            onClick={handleContinue}
            disabled={!canContinue}
            className={cn(
              "flex items-center gap-2 px-6 py-3 rounded-xl transition-all text-base font-medium",
              "bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg",
              "disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
            )}
          >
            <Check className="w-5 h-5" />
            <span>{stage === "testing" ? t("finish") : t("startTest")}</span>
          </button>
        </div>
      </div>
    </MainLayout>
  );
}
